/**
 * Sparkline — tiny SVG trend line with a terminal dot.
 * Self-contained; uses CSS variable strings for colors so dark mode works automatically.
 *
 * Flow: normalise data points → map to SVG coordinates → polyline + terminal circle.
 * RTL: when `dir="rtl"` the points are reversed so "latest" sits at the logical end (left).
 */
import { useDirection } from "@/shared/hooks/useDirection";

interface SparklineProps {
  points: number[];
  stroke?: string;
  height?: number;
  width?: number;
}

export function Sparkline({ points, stroke = "var(--terracotta)", height = 34, width = 120 }: SparklineProps) {
  const { dir } = useDirection();
  const p = 3;
  const ordered = dir === "rtl" ? [...points].reverse() : points;
  const mn = Math.min(...ordered);
  const mx = Math.max(...ordered);
  const rng = mx - mn || 1;
  const coords = ordered.map(
    (v, i) =>
      `${p + (i / (ordered.length - 1)) * (width - p * 2)},${height - p - ((v - mn) / rng) * (height - p * 2)}`,
  );
  const [lx, ly] = (coords[coords.length - 1] ?? "0,0").split(",");

  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
      <polyline
        points={coords.join(" ")}
        fill="none"
        stroke={stroke}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.75"
      />
      <circle cx={lx} cy={ly} r="2.8" fill={stroke} opacity="0.9" />
    </svg>
  );
}
